// useAuth — JWT w localStorage, prosty singleton listener.
//
// API:
//   const { token, team, isLoggedIn, isAdmin, login, logout } = useAuth();

import { useState, useEffect, useCallback, useReducer, useMemo } from 'react';
import { API_BASE } from '../config.js';
// E7a (MONIT 2026-10-13_1130): mały admin w trybie AK z aktywnym powiązaniem = ekrany widzą technika AK.
import { effectiveTeam, isViewerAccount, viewerAkModeOn, readViewerAkContext, linkedTeamOf, subscribeViewerAk } from '../shared/viewerAkMode.js';

function lsOrNull() {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}

const STORAGE_KEY = 'fiberops.session.v1';

// In-memory cache + pub/sub
let current = loadFromStorage();
const listeners = new Set();

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveToStorage(session) {
  if (!session) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }
}

function notify() {
  for (const l of listeners) {
    try { l(current); } catch {}
  }
}

export function getCurrentSession() {
  return current;
}

export function getToken() {
  return current?.token || null;
}

export function setSession(session) {
  current = session;
  saveToStorage(session);
  notify();
}

export function clearSession() {
  setSession(null);
}

/**
 * E6 (MONIT 2026-10-13_0950 pkt F): subskrypcja zmian sesji poza Reactem (magazyny w pamięci, np. AK Info,
 * czyszczą się przy wylogowaniu / zmianie konta). Zwraca funkcję odpięcia.
 */
export function subscribeSession(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useAuth() {
  const [session, setSessionState] = useState(current);

  useEffect(() => {
    const listener = (s) => setSessionState(s);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  // E7a: przełączenie MO / AK albo nowy kontekst AK małego admina = przerysowanie (rola ekranów się zmienia).
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => subscribeViewerAk(bump), []);

  const login = useCallback(async (pin) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Login failed (${res.status})`);
    }
    const data = await res.json();
    setSession({ token: data.token, team: data.team, loginAt: Date.now() });
    return data;
  }, []);

  // K19 (Martin): admin login — osobny endpoint /api/auth/admin/login z bcrypt PIN.
  const adminLogin = useCallback(async (pin) => {
    const res = await fetch(`${API_BASE}/api/auth/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Admin login failed (${res.status})`);
    }
    const data = await res.json();
    setSession({ token: data.token, team: data.team, loginAt: Date.now() });
    return data;
  }, []);

  const logout = useCallback(() => {
    clearSession();
  }, []);

  // E7a: `rawTeam` = konto z logowania; `team` = to, co widzą ekrany (mały admin jako team AK = technik AK).
  const rawTeam = session?.team || null;
  const storage = lsOrNull();
  const akCtx = isViewerAccount(rawTeam) ? readViewerAkContext(storage, rawTeam) : null;
  const akModeOn = viewerAkModeOn(rawTeam, storage);
  const akCtxKey = akCtx ? `${akCtx.savedAt || ''}|${linkedTeamOf(akCtx)?.id || ''}|${linkedTeamOf(akCtx)?.active ? 1 : 0}` : '';
  // Stała tożsamość obiektu między renderami (efekty ekranów z `team` w zależnościach nie ruszają w kółko).
  const team = useMemo(
    () => (rawTeam ? effectiveTeam(rawTeam, storage, akCtx) : null),
    [rawTeam, akModeOn, akCtxKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const viewerAkActing = !!rawTeam && team !== rawTeam;

  // FAZA 2: rola z body logowania (team.role). isAdmin pozostaje derived (wstecz-zgodne).
  // Fallback dla starych sesji bez .role: admin→'admin', technik→'technik'.
  const role = team?.role || (team?.isAdmin ? 'admin' : (session?.token ? 'technik' : null));

  return {
    token: session?.token || null,
    team,
    isLoggedIn: !!session?.token,
    isAdmin: !!team?.isAdmin,
    role,
    isViewerAdmin: role === 'viewer-admin',
    // E7a: konto małego admina (niezależnie od trybu), tryb AK, działanie jako team AK, powiązany team.
    isViewerAccount: isViewerAccount(rawTeam),
    accountName: rawTeam?.name || null,
    viewerAkMode: akModeOn,
    viewerAkActing,
    viewerAkLinked: linkedTeamOf(akCtx),
    // KROK 1/R2b: role serwisowe (support/support-plus) — gate'y toru wysyłkowego.
    isServiceRole: role === 'support' || role === 'support-plus',
    // AK E1 (MONIT 2026-10-10_1515): rodzaj teamu technika z body logowania ('mo' | 'ak');
    // stara sesja bez kind = montaż. Admin nie ma rodzaju (tryb MO / AK u Admina = E5).
    kind: team?.isAdmin ? null : (team?.kind || (session?.token ? 'mo' : null)),
    isAk: !!team && !team.isAdmin && team.kind === 'ak',
    login,
    adminLogin,
    logout,
  };
}
